import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiRequest, ApiError } from "../api/client";
import { FullScreenLoader } from "../components/common/FullScreenLoader";
import { Account, Category, CreditCard } from "../types/dashboard";

interface StatementImport {
  id: number;
}

function isFirstTimeUser(input: {
  accounts: Account[];
  cards: CreditCard[];
  categories: Category[];
  imports: StatementImport[];
}) {
  return input.accounts.length === 0 && input.cards.length === 0 && input.categories.length === 0 && input.imports.length === 0;
}

export default function DashboardEntryGate() {
  const { token, user, logout } = useAuth();
  const [targetPath, setTargetPath] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!token || !user) return;
    const sessionKey = `dashboard-entry-redirected-${user.id}`;
    if (sessionStorage.getItem(sessionKey) === "1") {
      setTargetPath("/dashboard/movimientos");
      return;
    }

    let cancelled = false;
    const resolveEntry = async () => {
      try {
        const [accounts, cards, categories, imports] = await Promise.all([
          apiRequest<Account[]>("/accounts", { token }),
          apiRequest<CreditCard[]>("/cards", { token }),
          apiRequest<Category[]>("/categories", { token }),
          apiRequest<StatementImport[]>("/imports", { token }),
        ]);
        const firstTime = isFirstTimeUser({ accounts, cards, categories, imports });
        if (cancelled) return;
        sessionStorage.setItem(sessionKey, "1");
        setTargetPath(firstTime ? "/dashboard/configuracion" : "/dashboard/analisis");
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) {
          logout();
          return;
        }
        sessionStorage.setItem(sessionKey, "1");
        setTargetPath("/dashboard/analisis");
      }
    };

    void resolveEntry();
    return () => {
      cancelled = true;
    };
  }, [token, user, logout]);

  if (!targetPath) {
    return <FullScreenLoader message="Preparando tu panel..." />;
  }

  return <Navigate to={targetPath} replace />;
}
